import { createSlice } from "@reduxjs/toolkit";

// Handles both HTTP (camelCase) and socket (snake_case) payloads
const mapNotification = (n) => ({
  id: n.id || n._id,
  title: n.title,
  notificationMessage: n.notificationMessage || n.notification_message,
  redirectionLink: n.redirectionLink || n.redirection_link,
  isActive: n.isActive ?? n.is_active,
  notificationExpireDate: n.notificationExpireDate || n.notification_expire_date,
  createdAt: n.createdAt || n.created_at,
  read: n.read || n.isRead || false,
  UserNotificationType:
    n.UserNotificationType ||
    n.userNotificationType ||
    n.user_notification_type ||
    null,
  referenceType: n.referenceType || n.reference_type,
});

const initialState = {
  loading: false,
  readLoading: false,
  error: null,
  notifications: [],
  unreadCount: 0,
  pagination: {
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
  },
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    getNotificationsStart: (state, action) => {
      state.loading = true;
      state.error = null;
      if (action.payload?.pageNumber === 1 || !action.payload?.pageNumber) {
        state.notifications = [];
      }
    },
    getNotificationsSuccess: (state, action) => {
      state.loading = false;

      const raw = Array.isArray(action.payload)
        ? action.payload
        : action.payload?.data || action.payload?.notifications || [];

      const pagination = action.payload?.pagination;
      if (pagination) {
        state.pagination = pagination;
      }

      const mapped = raw
        .filter((n) => n.isActive !== false)
        .map(mapNotification)
        .filter((notif) => !notif.read);

      if (pagination?.page > 1) {
        state.notifications = [...state.notifications, ...mapped];
      } else {
        state.notifications = mapped;
      }

      state.unreadCount = state.notifications.length;
    },
    getNotificationsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    markNotificationAsRead: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find((n) => n.id === notificationId);
      if (notification) {
        notification.read = true;
        notification.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach((notification) => {
        notification.read = true;
        notification.isRead = true;
      });
      state.unreadCount = 0;
    },
    resetNotificationsState: () => ({ ...initialState }),
    readNotificationStart: (state, action) => {
      state.readLoading = true;
      state.error = null;
      const notificationIds =
        action.payload.notificationIds ||
        (action.payload.notificationId ? [action.payload.notificationId] : []);
      state.notifications = state.notifications.filter(
        (notif) =>
          !notificationIds.includes(String(notif.id)) &&
          !notificationIds.includes(String(notif._id))
      );
      state.unreadCount = state.notifications.length;
      state.pagination.totalRecords = Math.max(
        0,
        state.pagination.totalRecords - notificationIds.length
      );
    },
    readNotificationSuccess: (state) => {
      state.readLoading = false;
    },
    readNotificationFailure: (state, action) => {
      state.readLoading = false;
      state.error = action.payload;
    },
    // Trigger action — saga listens to this to start the socket connection
    startNotificationSocket: () => {},
    addSocketNotification: (state, action) => {
      // Payload may be { notificationData: {...} }, { userId, notificationData: {...} }, or a bare object/array
      const unwrap = (item) => (item && item.notificationData ? item.notificationData : item);
      const raw = Array.isArray(action.payload)
        ? action.payload.map(unwrap)
        : [unwrap(action.payload)];
      const incoming = raw
        .filter((n) => n && n.isActive !== false && n.is_active !== false)
        .map(mapNotification)
        .filter((n) => !n.read);

      const existingIds = new Set(state.notifications.map((n) => String(n.id)));
      const newOnes = incoming.filter((n) => !existingIds.has(String(n.id)));

      if (newOnes.length === 0) return;

      state.notifications = [...newOnes, ...state.notifications];
      state.unreadCount = state.notifications.length;
      state.pagination.totalRecords += newOnes.length;
    },
  },
});

export const {
  getNotificationsStart,
  getNotificationsSuccess,
  getNotificationsFailure,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  resetNotificationsState,
  readNotificationStart,
  readNotificationSuccess,
  readNotificationFailure,
  startNotificationSocket,
  addSocketNotification,
} = notificationsSlice.actions;

export const selectNotifications = (state) => state.notifications;

export default notificationsSlice.reducer;
